import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Monitor } from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import FormField from '../../components/common/FormField';
import RequiredLabel from '../../components/common/RequiredLabel';
import FormSelectVirtualized from '../../components/common/FormSelectVirtualized';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import { trimFormStringFields } from '../../utils/trimFormStringFields';
import { mapFormValuesToUpsertPayload, mapMachineToFormValues } from '../../utils/machineMapper';
import {
  getMachineFormFieldErrors,
  hasMachineFormErrors,
} from '../../validation/machineFormValidation';
import machineService from '../../services/machineService';
import { formatRelativeTimeAgo } from '../../utils/dateFormatter';
import type {
  Machine,
  MachineConnectivityStatus,
  MachineFormValues,
} from '../../types/machine.types';
import './MachineManagement.css';

interface MachineEditPanelProps {
  machine: Machine;
  onClose: () => void;
  onSuccess: (machine: Machine) => void;
  onConnectivityUpdate?: (machine: Machine) => void;
  modalityOptions: { value: string; label: string }[];
  existingMachines: Machine[];
}

function MachineConnectivitySummary({
  machine,
  checking,
}: Readonly<{
  machine: Machine;
  checking: boolean;
}>) {
  if (checking) {
    return (
      <div className="machine-connectivity-summary">
        <p className="machine-connectivity-summary-status">Checking connectivity…</p>
      </div>
    );
  }

  const { connectivityStatus, responseTime, failureReason, lastCheckedAt } = machine;
  if (connectivityStatus === 'Unknown' && !lastCheckedAt) {
    return null;
  }

  const statusLabel: Record<MachineConnectivityStatus, string> = {
    Online: 'Online',
    Offline: 'Offline',
    ConfigurationError: 'Configuration error',
    Unknown: 'Unknown',
    Checking: 'Checking',
  };

  return (
    <div className="machine-connectivity-summary">
      <p className="machine-connectivity-summary-status">
        Status: <strong>{statusLabel[connectivityStatus]}</strong>
        {connectivityStatus === 'Online' && responseTime != null ? ` (${responseTime}ms)` : null}
      </p>
      {machine.modality || machine.ipAddress ? (
        <p className="machine-connectivity-summary-detail">
          {[machine.modality, machine.ipAddress].filter(Boolean).join(' · ')}
        </p>
      ) : null}
      {lastCheckedAt ? (
        <p className="machine-connectivity-summary-detail">
          Last checked {formatRelativeTimeAgo(lastCheckedAt)}
        </p>
      ) : null}
      {machine.connectivityStatus === 'Offline' && failureReason ? (
        <p className="machine-connectivity-summary-error">{failureReason}</p>
      ) : null}
      {machine.connectivityStatus === 'ConfigurationError' && failureReason ? (
        <p className="machine-connectivity-summary-error">{failureReason}</p>
      ) : null}
    </div>
  );
}

const MachineEditPanel: React.FC<MachineEditPanelProps> = ({
  machine,
  onClose,
  onSuccess,
  onConnectivityUpdate,
  modalityOptions,
  existingMachines,
}) => {
  const [values, setValues] = useState<MachineFormValues>(() => mapMachineToFormValues(machine));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof MachineFormValues, string>>>(
    {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [checkingConnectivity, setCheckingConnectivity] = useState(false);
  const [connectivityError, setConnectivityError] = useState<string | null>(null);

  useEffect(() => {
    setValues(mapMachineToFormValues(machine));
    setFieldErrors({});
    setSubmitError(null);
    setConnectivityError(null);
  }, [machine]);

  const setField = useCallback((field: keyof MachineFormValues, value: string | boolean) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleSubmit = async () => {
    const trimmed = trimFormStringFields(
      values as unknown as Record<string, unknown>
    ) as unknown as MachineFormValues;
    const errors = getMachineFormFieldErrors(trimmed, existingMachines, machine.id);
    setFieldErrors(errors);
    if (hasMachineFormErrors(errors)) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = mapFormValuesToUpsertPayload(trimmed);
      const result = await machineService.saveMachineWithStatus(
        machine.id,
        payload,
        machine.active,
        trimmed.active
      );
      if (result.success && result.data) {
        onSuccess(result.data);
      } else {
        setSubmitError(result.error || 'Failed to save changes.');
      }
    } catch {
      setSubmitError('Failed to save changes.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckConnectivity = useCallback(async () => {
    setCheckingConnectivity(true);
    setConnectivityError(null);

    try {
      const result = await machineService.checkMachineConnectivity(machine.id, machine);
      if (result.success && result.data) {
        onConnectivityUpdate?.(result.data);
      } else {
        setConnectivityError(result.error || 'Failed to check connectivity.');
      }
    } catch {
      setConnectivityError('Failed to check connectivity.');
    } finally {
      setCheckingConnectivity(false);
    }
  }, [machine, onConnectivityUpdate]);

  const modalitySelectOptions = useMemo(
    () => modalityOptions.map(o => ({ value: o.value, label: o.label })),
    [modalityOptions]
  );

  const subtitle = `${machine.modality || values.modalityType} · ${machine.aeTitle || values.aeTitle}`;

  return (
    <aside className="machine-edit-panel">
      <header className="machine-edit-panel-header">
        <div className="machine-edit-panel-header-main">
          <div
            className="machine-edit-panel-icon"
            aria-hidden
          >
            <Monitor
              size={18}
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </div>
          <div>
            <h2 className="machine-edit-panel-title">{machine.machineName}</h2>
            <p className="machine-edit-panel-subtitle">{subtitle}</p>
          </div>
        </div>
        <button
          type="button"
          className="machine-edit-panel-close"
          onClick={onClose}
          aria-label="Close"
        >
          <X
            size={20}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        </button>
      </header>

      <div className="machine-form-body machine-edit-panel-body">
        <div className="machine-form-field">
          <RequiredLabel
            label="Machine name"
            required
            htmlFor="edit-machine-name"
          />
          <FormField
            id="edit-machine-name"
            label=""
            value={values.machineName}
            onChange={e => setField('machineName', e.target.value)}
            error={fieldErrors.machineName}
          />
        </div>

        <div className="machine-form-row machine-form-row--modality">
          <div className="machine-form-field">
            <FormSelectVirtualized
              label="Modality type"
              required
              name="editModalityType"
              options={modalitySelectOptions}
              value={values.modalityType}
              onChange={e => setField('modalityType', e.target.value)}
              error={fieldErrors.modalityType}
            />
          </div>
          <div className="machine-form-field">
            <RequiredLabel
              label="Location"
              required
              htmlFor="edit-machine-location"
            />
            <FormField
              id="edit-machine-location"
              label=""
              value={values.location}
              onChange={e => setField('location', e.target.value)}
              error={fieldErrors.location}
            />
          </div>
        </div>

        <div className="machine-form-field">
          <RequiredLabel
            label="AE title"
            required
            htmlFor="edit-machine-ae-title"
          />
          <FormField
            id="edit-machine-ae-title"
            label=""
            value={values.aeTitle}
            onChange={e => setField('aeTitle', e.target.value.toUpperCase())}
            error={fieldErrors.aeTitle}
          />
        </div>

        <div className="machine-form-row machine-form-row--ip">
          <div className="machine-form-field">
            <RequiredLabel
              label="IP address"
              required
              htmlFor="edit-machine-ip"
            />
            <FormField
              id="edit-machine-ip"
              label=""
              value={values.ipAddress}
              onChange={e => setField('ipAddress', e.target.value)}
              error={fieldErrors.ipAddress}
            />
          </div>
          <div className="machine-form-field machine-form-field--port">
            <RequiredLabel
              label="Port"
              required
              htmlFor="edit-machine-port"
            />
            <FormField
              id="edit-machine-port"
              label=""
              value={values.port}
              onChange={e => setField('port', e.target.value)}
              error={fieldErrors.port}
            />
          </div>
        </div>

        <div className="machine-form-active-row">
          <ToggleSwitch
            id="machine-edit-active"
            checked={values.active}
            onChange={checked => setField('active', checked)}
          />
          <label
            htmlFor="machine-edit-active"
            className="machine-form-active-label"
          >
            Active
          </label>
        </div>
        <p className="machine-form-active-helper">
          Inactive machines are excluded from live DICOM routing.
        </p>

        <div className="machine-connectivity-actions">
          <button
            type="button"
            className="machine-dialog-btn machine-dialog-btn-cancel"
            onClick={handleCheckConnectivity}
            disabled={checkingConnectivity || submitting}
          >
            {checkingConnectivity ? 'Checking connectivity…' : 'Check connectivity'}
          </button>
        </div>
        <MachineConnectivitySummary
          machine={machine}
          checking={checkingConnectivity}
        />
        {connectivityError ? (
          <p className="machine-form-submit-error">{connectivityError}</p>
        ) : null}

        {submitError ? <p className="machine-form-submit-error">{submitError}</p> : null}
      </div>

      <footer className="machine-edit-panel-footer">
        <button
          type="button"
          className="machine-dialog-btn machine-dialog-btn-cancel"
          onClick={onClose}
        >
          Close
        </button>
        <button
          type="button"
          className="machine-dialog-btn machine-dialog-btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save changes'}
        </button>
      </footer>
    </aside>
  );
};

export default MachineEditPanel;
